import { DishRatingFilter } from 'common/DishRatingFilter';
import { between, lte } from 'common/PatternMatchingUtilities';
import { QueryParams } from 'common/urls/QueryParams';
import React, { useCallback, useState } from 'react';
import { match } from 'ts-pattern';
import { useTrackFilterChange } from '../hooks/useTrackFilterChange';
import { SECTION_TEXT } from './consts';
import { RatingSlider, Section } from './styles';
import { FilterProps } from './types';

const DISH_RATING_TO_VALUE: Record<DishRatingFilter, number> = {
  [DishRatingFilter.SIXTY_PERCENT_PLUS]: 0,
  [DishRatingFilter.SEVENTY_PERCENT_PLUS]: 33,
  [DishRatingFilter.EIGHTY_PERCENT_PLUS]: 66,
  [DishRatingFilter.NINETY_PERCENT_PLUS]: 100,
};

const VALUE_TO_DISH_RATING: Record<number, DishRatingFilter> = {
  0: DishRatingFilter.SIXTY_PERCENT_PLUS,
  33: DishRatingFilter.SEVENTY_PERCENT_PLUS,
  66: DishRatingFilter.EIGHTY_PERCENT_PLUS,
  100: DishRatingFilter.NINETY_PERCENT_PLUS,
};

export const getClosestValue = (value: string) => {
  const numberValue = parseInt(value, 10);

  // Use approximate midpoints between values to "snap" to closest rating filter (e.g. midpoint for 0 to 33 is 16)
  return match(numberValue)
    .with(lte(16), () => DISH_RATING_TO_VALUE[DishRatingFilter.SIXTY_PERCENT_PLUS])
    .with(between(16, 50), () => DISH_RATING_TO_VALUE[DishRatingFilter.SEVENTY_PERCENT_PLUS])
    .with(between(49, 83), () => DISH_RATING_TO_VALUE[DishRatingFilter.EIGHTY_PERCENT_PLUS])
    .otherwise(() => DISH_RATING_TO_VALUE[DishRatingFilter.NINETY_PERCENT_PLUS]);
};

const isFilterSelected = (value: string, dishRating: DishRatingFilter) =>
  parseInt(value, 10) === DISH_RATING_TO_VALUE[dishRating];

export const DishRating: React.FC<FilterProps> = ({ filters }) => {
  const { minRatingFilter, updateQueryParams } = filters;
  const [currentRating, setCurrentRating] = useState(`${DISH_RATING_TO_VALUE[minRatingFilter]}`);
  const { trackFilterChange } = useTrackFilterChange({ filters });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setCurrentRating(value);
      const closestValue = getClosestValue(value);
      const updates = {
        [QueryParams.MIN_RATING_FILTER]: VALUE_TO_DISH_RATING[closestValue],
      };
      updateQueryParams(updates);
      trackFilterChange(updates);
    },
    [updateQueryParams, trackFilterChange]
  );

  return (
    <Section.Container>
      <Section.Header>{SECTION_TEXT.DISH_RATING.HEADER}</Section.Header>
      <Section.Wrapper>
        <RatingSlider.Container>
          <RatingSlider.BarContainer>
            <RatingSlider.BarBackground />
            <RatingSlider.Bar style={{ width: `${currentRating}%` }} />
            <RatingSlider.ScrubberContainer>
              <RatingSlider.Scrubber style={{ left: `${currentRating}%` }} />
            </RatingSlider.ScrubberContainer>
          </RatingSlider.BarContainer>
          <RatingSlider.LabelContainer>
            <RatingSlider.Label isSelected={isFilterSelected(currentRating, DishRatingFilter.SIXTY_PERCENT_PLUS)}>
              <RatingSlider.LabelIcon />
              {SECTION_TEXT.DISH_RATING.RATING[1]}
            </RatingSlider.Label>
            <RatingSlider.Label isSelected={isFilterSelected(currentRating, DishRatingFilter.SEVENTY_PERCENT_PLUS)}>
              <RatingSlider.LabelIcon />
              {SECTION_TEXT.DISH_RATING.RATING[2]}
            </RatingSlider.Label>
            <RatingSlider.Label isSelected={isFilterSelected(currentRating, DishRatingFilter.EIGHTY_PERCENT_PLUS)}>
              <RatingSlider.LabelIcon />
              {SECTION_TEXT.DISH_RATING.RATING[3]}
            </RatingSlider.Label>
            <RatingSlider.Label isSelected={isFilterSelected(currentRating, DishRatingFilter.NINETY_PERCENT_PLUS)}>
              <RatingSlider.LabelIcon />
              {SECTION_TEXT.DISH_RATING.RATING[4]}
            </RatingSlider.Label>
          </RatingSlider.LabelContainer>
          <RatingSlider.Input value={currentRating} onChange={handleChange} step={'1'} />
        </RatingSlider.Container>
      </Section.Wrapper>
    </Section.Container>
  );
};
